import { useEffect } from 'react';
import { useCredits } from '../contexts/CreditsContext';

/**
 * Global Premium Card Click Handler Hook
 * 
 * Intercepts clicks on any premium card across the platform and shows
 * the insufficient credits modal if the user doesn't have enough credits.
 * 
 * Usage: Call this hook once in App.js or root component
 */
const useGlobalPremiumHandler = (setShowModal, setModalData) => {
    const { creditBalance, hasSufficientCredits } = useCredits();

    useEffect(() => {
        const handleGlobalClick = (e) => {
            // Find the closest premium card element
            const premiumCard = e.target.closest('[data-premium="true"]');
            
            if (!premiumCard) return;

            // Get required credits from data attribute
            const requiredCredits = parseInt(premiumCard.getAttribute('data-required-credits') || '1', 10);

            // Check if user has sufficient credits
            if (!hasSufficientCredits(requiredCredits)) {
                // Prevent default action and stop propagation
                e.preventDefault();
                e.stopPropagation();

                // Extract card data for modal
                const cardTitle = premiumCard.querySelector('.track-title, .video-title')?.textContent || 'this content';
                const cardType = premiumCard.classList.contains('track-item') ? 'track' : 
                                premiumCard.classList.contains('video-item') ? 'video' : 'content';

                // Show the insufficient credits modal
                setModalData({
                    requiredCredits,
                    currentCredits: creditBalance,
                    contentTitle: cardTitle,
                    contentType: cardType
                });
                setShowModal(true);
            }
        };

        // Add global click listener
        document.addEventListener('click', handleGlobalClick, true); // Use capture phase

        // Cleanup
        return () => {
            document.removeEventListener('click', handleGlobalClick, true);
        };
    }, [creditBalance, hasSufficientCredits, setShowModal, setModalData]);
};

export default useGlobalPremiumHandler;
